import {DestroyRef, inject, Injectable, signal} from "@angular/core";
import {BreakpointObserver, BreakpointState} from "@angular/cdk/layout";
import {UntilDestroy, untilDestroyed} from "@ngneat/until-destroy";
import {takeUntilDestroyed, toSignal} from "@angular/core/rxjs-interop";
import {OrientationBreakpoints} from "../../../shared/models/SimDeviceBreakpoints";
import {map} from "rxjs/operators";

export enum LayoutVariants {
	"TABLET_LANDSCAPE",
	"TABLET_PORTRAIT",
	"DESKTOP",
}

@UntilDestroy()
@Injectable({
	providedIn: "root",
})
export class DeviceViewService {
	public layout: LayoutVariants = LayoutVariants.DESKTOP;
	readonly #breakpointObserver = inject(BreakpointObserver);
	readonly #orientationObserver = inject(BreakpointObserver);
	readonly #destroyRef = inject(DestroyRef);

	constructor() {
		this.#breakpointObserver
			.observe(["(max-width: 1400px)", "(orientation: landscape)"])
			.pipe(untilDestroyed(this))
			.subscribe((state: BreakpointState) => {
				if (state.breakpoints["(max-width: 1400px)"] && state.breakpoints["(orientation: landscape)"]) {
					this.layout = LayoutVariants.TABLET_LANDSCAPE;
				} else {
					this.layout = LayoutVariants.DESKTOP;
				}
			});

		this.#breakpointObserver
			.observe(["(orientation: portrait)"])
			.pipe(untilDestroyed(this))
			.subscribe((state: BreakpointState) => {
				if (state.matches) {
					this.layout = LayoutVariants.TABLET_PORTRAIT;
				}
			});
	}

	public readonly orientation = toSignal<"landscape" | "portrait">(
		this.#orientationObserver.observe([OrientationBreakpoints.Landscape, OrientationBreakpoints.Portrait]).pipe(
			takeUntilDestroyed(this.#destroyRef),
			map((value) => {
				const breakpoints = value.breakpoints;
				return breakpoints[OrientationBreakpoints.Landscape] ? "landscape" : "portrait";
			}),
		),
	);

	public readonly isTouchDevice = signal<boolean>("ontouchstart" in window || navigator.maxTouchPoints > 0);
}
